import { inject, Injectable } from '@angular/core';

import { HttpService } from '../../core/services';
import type { TaskProjectUpsertPayload, TaskSprintUpsertPayload, TaskTimeEntryCreatePayload, TaskTimerStartPayload, TaskTimerStopPayload, TaskUpsertPayload } from './task.models';

@Injectable({
  providedIn: 'root',
})
export class TaskApi {
  private readonly http = inject(HttpService);

  listProjects() {
    return this.http.get('tasks/projects');
  }

  createProject(payload: TaskProjectUpsertPayload) {
    return this.http.post('tasks/projects', payload);
  }

  updateProject(projectId: string, payload: TaskProjectUpsertPayload) {
    return this.http.put(`tasks/projects/${projectId}`, payload);
  }

  listSprints(projectId: string) {
    return this.http.get(`tasks/projects/${projectId}/sprints`);
  }

  createSprint(projectId: string, payload: TaskSprintUpsertPayload) {
    return this.http.post(`tasks/projects/${projectId}/sprints`, payload);
  }

  updateSprint(projectId: string, sprintId: string, payload: TaskSprintUpsertPayload) {
    return this.http.put(`tasks/projects/${projectId}/sprints/${sprintId}`, payload);
  }

  listTasks(projectId?: string) {
    const query = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '';
    return this.http.get(`tasks${query}`);
  }

  listNotifications() {
    return this.http.get('task-notifications');
  }

  markNotificationRead(notificationId: string) {
    return this.http.put(`task-notifications/${notificationId}/read`, {});
  }

  markAllNotificationsRead() {
    return this.http.put('task-notifications/read-all', {});
  }

  clearNotifications() {
    return this.http.delete('task-notifications');
  }

  getTask(taskId: string) {
    return this.http.get(`tasks/${taskId}`);
  }

  createTask(payload: TaskUpsertPayload) {
    return this.http.post('tasks', payload);
  }

  updateTask(taskId: string, payload: TaskUpsertPayload) {
    return this.http.put(`tasks/${taskId}`, payload);
  }

  addComment(taskId: string, payload: { body: string }) {
    return this.http.post(`tasks/${taskId}/comments`, payload);
  }

  addSubtask(taskId: string, payload: { title: string; assignee_user_id?: string }) {
    return this.http.post(`tasks/${taskId}/subtasks`, payload);
  }

  updateSubtask(taskId: string, subtaskId: string, payload: { title?: string; is_completed?: boolean; assignee_user_id?: string }) {
    return this.http.put(`tasks/${taskId}/subtasks/${subtaskId}`, payload);
  }

  deleteSubtask(taskId: string, subtaskId: string) {
    return this.http.delete(`tasks/${taskId}/subtasks/${subtaskId}`);
  }

  uploadAttachment(taskId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.postForm(`tasks/${taskId}/attachments`, formData);
  }

  addTimeEntry(taskId: string, payload: TaskTimeEntryCreatePayload) {
    return this.http.post(`tasks/${taskId}/time-entries`, payload);
  }

  updateTimeEntry(taskId: string, timeEntryId: string, payload: TaskTimeEntryCreatePayload) {
    return this.http.put(`tasks/${taskId}/time-entries/${timeEntryId}`, payload);
  }

  deleteTimeEntry(taskId: string, timeEntryId: string) {
    return this.http.delete(`tasks/${taskId}/time-entries/${timeEntryId}`);
  }

  startTimer(taskId: string, payload: TaskTimerStartPayload = {}) {
    return this.http.post(`tasks/${taskId}/timer/start`, payload);
  }

  stopTimer(taskId: string, payload: TaskTimerStopPayload = {}) {
    return this.http.post(`tasks/${taskId}/timer/stop`, payload);
  }
}
